import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid';
import SelfAdjustingTimer from '../../../utils/SelfAdjustingTimer';
import { State, addStateChangeListener, getState, removeStateChangeListener, updateState, } from '../../../utils/applicationState';
import networkUrlBuilder from '../../../utils/networkUrlBuilder';
import logger from '../../../logging/logger';
import googlePal from '../../../targeting/palSdkController';
import { PlayStates } from '../../../utils/constants';
import reporter from '../../reporter';
import Events from '../../media/Events';
import mediaEvents from '../../media/mediaEvents';
import InstreamCompanionController from './InstreamCompanionController';
const ONE_SECOND = 1000;
export default class InstreamAvailsController {
    constructor() {
        this.setInitialState();
    }
    setInitialState = () => {
        this.isInitiated = false;
        this.queuedAdCollection = {};
        this.timedEventMap = {};
        this.actionEventListeners = {};
        this.currentAdId = undefined;
        this.currentSessionTime = 0;
        this.paused = false;
        this.sessionTimer = undefined;
        this.canSendEvents = true;
        this.adList = [];
        this.currentAdBreakId = undefined;
        this.companionController = undefined;
    };
    destroy = () => {
        removeStateChangeListener(State.tuneRequested, this.onTuneRequested);
        removeStateChangeListener(State.playerStatus, this.onPlayerStatusChange);
        if (this.companionController) {
            this.companionController.destroy();
        }
        forEach(this.queuedAdCollection, (ad) => this.removeAdFromQueue(ad.adId));
        this.resetSessionTimer();
        this.setInitialState();
    };
    processAdData = ({ avails, playerPosition, forceUpdate }) => {
        if (this.isInitiated && (!avails || avails.length < 1)) {
            this.destroy();
            return;
        }
        if (!this.isInitiated) {
            this.sessionTimer = new SelfAdjustingTimer(this.onTimeChange, ONE_SECOND);
            addStateChangeListener(State.tuneRequested, this.onTuneRequested);
            addStateChangeListener(State.playerStatus, this.onPlayerStatusChange);
            this.companionController = new InstreamCompanionController();
            this.isInitiated = true;
        }
        this.updateSessionTime(this.getPlayerTime(playerPosition));
        const currentTime = this.currentSessionTime;
        const adBreak = this.getCurrentOrNextAdBreak(avails, currentTime);
        if (!adBreak) {
            return;
        }
        if (!adBreak.ads || adBreak.ads.length < 1) {
            this.destroy();
        }
        const hasAdsInQueue = !isEmpty(this.queuedAdCollection);
        if (!(hasAdsInQueue && this.currentAdBreakId)) {
            this.currentAdBreakId = adBreak.availId;
        }
        else if (this.currentAdBreakId !== adBreak.availId) {
            return;
        }
        if (getState(State.tuneRequested) === true) {
            updateState(State.tuneRequested, false);
        }
        this.adList = adBreak.ads;
        if (hasAdsInQueue && forceUpdate) {
            this.clearQueuedAds(this.currentAdId);
        }
        if (this.currentAdId) {
            const currentAdIndex = findIndex(this.adList, (ad) => this.currentAdId === ad.adId);
            if (currentAdIndex !== -1) {
                this.attemptToQueueNext(currentAdIndex);
                return;
            }
        }
        const currentOrNext = this.getCurrentOrNextAd(this.adList, currentTime);
        if (!currentOrNext.ad) {
            return;
        }
        mediaEvents.event(Events.Type.instream, Events.Status.response).emit({
            ad: {
                ...currentOrNext.ad,
                adProvider: get(currentOrNext.ad, 'companionAds', []).length < 1
                    ? 'dfp'
                    : 'unknown',
                adUnitId: get(this.companionController, 'adUnitIds.audio'),
            },
        });
        logger.debug(`add the 1st valid ad (${currentOrNext.ad.adId}) from the response`);
        this.addToQueueOrUpdate(currentOrNext.ad);
        this.attemptToQueueNext(currentOrNext.index);
    };
    clearQueuedAds = (currentAdIdOnForceUpdate) => {
        this.timedEventMap = {};
        forEach(this.queuedAdCollection, (ad) => {
            if (currentAdIdOnForceUpdate !== ad.adId) {
                this.removeAdFromQueue(ad.adId);
            }
        });
        const currentAd = this.queuedAdCollection[currentAdIdOnForceUpdate];
        if (currentAd) {
            logger.debug(`[adId: ${currentAdIdOnForceUpdate}] forceUpdate, re-adding time events...`);
            this.setupTrackingEvents(currentAd);
            this.setupEndOfAdEvents(currentAd);
        }
    };
    addToQueueOrUpdate = (ad) => {
        if (isEmpty(this.queuedAdCollection)) {
            this.startSessionTimer();
        }
        if (this.queuedAdCollection[ad.adId]) {
            return;
        }
        this.queuedAdCollection[ad.adId] = ad;
        this.setupTrackingEvents(ad);
        if (ad.startTimeInSeconds <= this.currentSessionTime) {
            logger.debug(`[adId: ${ad.adId}] tracking in progress, started at ${Math.floor(ad.startTimeInSeconds)}`);
            this.startAd(ad);
        }
        else {
            logger.debug(`[adId ${ad.adId}] tracking will start at ${Math.floor(ad.startTimeInSeconds)}`);
            this.addToTimedEventMap(ad.startTimeInSeconds, {
                name: 'startAd',
                positionEventCallback: () => this.startAd(ad),
            });
        }
    };
    attemptToQueueNext = (index) => {
        if (this.adList.length <= 1) {
            return;
        }
        const nextAd = this.adList[index + 1];
        if (!nextAd) {
            return;
        }
        logger.debug(`add the next ad (${nextAd.adId}) from the response`);
        this.addToQueueOrUpdate(nextAd);
    };
    startAd = (ad) => {
        const { adId, durationInSeconds, trackingEvents } = ad || {};
        if (this.currentAdId === adId) {
            return;
        }
        logger.debug(`${adId} starting now, adbreak ${this.currentAdBreakId}`);
        if (this.paused) {
            this.paused = false;
        }
        this.currentAdId = adId;
        this.companionController.handleCompanionAds(ad);
        this.setupEndOfAdEvents(ad);
        forEach(this.actionEventListeners[this.currentAdId], (actionEvent) => {
            actionEvent.addListener();
        });
        this.companionController.set('currentAdRequestId', uuidv4());
        const { adProvider } = this.companionController || {};
        const adUnitId = get(this.companionController, 'adUnitIds.audio');
        this.companionController.set('audioSlotData', {
            name: 'instream',
            medium: 'audio',
            requestId: this.companionController.currentAdRequestId,
            formatDefinition: {
                adProvider,
                adUnitId,
            },
        });
        const startEvent = find(trackingEvents, ['eventType', 'start']);
        const { eventId } = startEvent || {};
        reporter.impression(this.companionController.audioSlotData);
        this.invokeReporting({
            eventStatus: Events.Status.started,
            eventId,
            durationInSeconds,
        });
        googlePal.reportAdImpression();
        this.companionController.showCompanionAds();
    };
    addToTimedEventMap(timeToInvoke, eventHandlerInfo) {
        if (timeToInvoke < this.currentSessionTime) {
            return;
        }
        const flooredTimeToInvoke = Math.floor(timeToInvoke);
        if (!this.timedEventMap[flooredTimeToInvoke]) {
            this.timedEventMap[flooredTimeToInvoke] = [];
        }
        this.timedEventMap[flooredTimeToInvoke].push(eventHandlerInfo);
    }
    setupTrackingEvents = (ad) => {
        const { trackingEvents } = ad;
        if (!trackingEvents || trackingEvents.length < 1) {
            return;
        }
        this.actionEventListeners[ad.adId] = [];
        forEach(trackingEvents, (event) => {
            const handlerCreator = this.eventTypeHandlerMap[event.eventType];
            const handlerInfo = handlerCreator?.(ad, event);
            if (handlerInfo) {
                if (handlerInfo.invokedByPosition) {
                    this.addToTimedEventMap(event.startTimeInSeconds, handlerInfo);
                }
                else {
                    this.actionEventListeners[ad.adId].push(handlerInfo);
                }
            }
        });
    };
    setupEndOfAdEvents = (ad) => {
        const adEndTime = ad.startTimeInSeconds + ad.durationInSeconds;
        const timeToPollForNextAd = adEndTime - 5;
        this.addToTimedEventMap(timeToPollForNextAd, {
            name: 'pollForNextAd',
            positionEventCallback: this.pollForNextAd,
        });
        this.addToTimedEventMap(adEndTime, {
            name: 'tearDownAd',
            positionEventCallback: () => this.tearDownAd(ad.adId),
        });
    };
    pollForNextAd = () => {
        logger.debug('poll for next ad');
        mediaEvents
            .event(Events.Type.instream, Events.Status.requestInstreamAds)
            .emit();
    };
    tearDownAd = (adId) => {
        logger.debug(`teardown ad: ${adId}`);
        this.removeAdFromQueue(adId);
        if (adId === this.currentAdId) {
            this.currentAdId = undefined;
        }
        if (isEmpty(this.queuedAdCollection)) {
            this.handleEndOfAdBreak();
        }
    };
    removeAdFromQueue = (adId) => {
        forEach(this.actionEventListeners[adId], (handler) => {
            if (handler.removeListener) {
                handler.removeListener();
            }
        });
        this.actionEventListeners[adId] = [];
        delete this.queuedAdCollection[adId];
    };
    handleEndOfAdBreak = () => {
        logger.debug('end of ad break');
        this.currentAdBreakId = undefined;
        this.companionController.resetCompanionsIfNeeded();
        this.pollForNextAd();
    };
    startSessionTimer = () => {
        this.resetSessionTimer();
        this.sessionTimer.start();
    };
    resetSessionTimer = () => {
        this.timedEventMap = {};
        if (this.sessionTimer) {
            this.sessionTimer.stop();
        }
    };
    updateSessionTime = (refreshedSessionTime) => {
        if (this.currentSessionTime < refreshedSessionTime &&
            !isEmpty(this.queuedAdCollection)) {
            const numberOfSkippedSeconds = refreshedSessionTime - this.currentSessionTime;
            for (let i = 0; i < numberOfSkippedSeconds; i++) {
                this.onTimeChange();
            }
        }
        else {
            this.currentSessionTime = refreshedSessionTime;
        }
    };
    getPlayerTime = (playerPosition = 0) => {
        return playerPosition > 0
            ? playerPosition
            : getState(State.positionInfo).elapsedSeconds || 0;
    };
    onTimeChange = () => {
        logger.debug(`Instream session timer: ${this.currentSessionTime}`);
        const eventsToFireNow = this.timedEventMap[this.currentSessionTime];
        if (eventsToFireNow) {
            if (this.canSendEvents) {
                forEach(eventsToFireNow, (handler) => {
                    handler.positionEventCallback();
                });
            }
            else {
                logger.debug(`Unable to send events because status = ${getState(State.playerStatus)}`);
            }
            delete this.timedEventMap[this.currentSessionTime];
        }
        this.currentSessionTime = this.currentSessionTime + 1;
    };
    onPlayerStatusChange = (status) => {
        this.canSendEvents =
            status === PlayStates.playing || status === PlayStates.connecting;
        if (status === PlayStates.stopped || status === PlayStates.error) {
            this.destroy();
        }
    };
    onTuneRequested = (wasRequested) => {
        if (wasRequested) {
            this.destroy();
        }
    };
    updateInstreamParams = () => {
        mediaEvents
            .event(Events.Type.instream, Events.Status.updateInstreamParams)
            .emit(networkUrlBuilder.getInstreamCustomParams());
    };
    invokeTracking = (event) => {
        const trackingUrls = event.beaconUrls || [];
        forEach(trackingUrls, (url) => {
            logger.debug(`tracking event '${event.eventType}' fired`);
            fetch(url, { mode: 'no-cors' });
        });
    };
    invokeReporting = ({ eventStatus, eventId, durationInSeconds }) => {
        const { adProvider } = this.companionController || {};
        const adUnitId = get(this.companionController, 'adUnitIds.audio');
        const adData = {
            adProvider,
            adUnitId,
        };
        if (durationInSeconds) {
            adData.durationInSeconds = durationInSeconds;
        }
        mediaEvents.event(Events.Type.instream, eventStatus).emit({
            ad: adData,
            eventId,
        });
    };
    getPauseHandler = (event) => {
        return (playerStatus) => {
            if (this.paused === false && playerStatus === Events.Status.paused) {
                this.invokeTracking(event);
                this.paused = true;
            }
        };
    };
    getResumeHandler = (event) => {
        return (playerStatus) => {
            if (this.paused === true && playerStatus === Events.Status.playing) {
                this.invokeTracking(event);
                this.paused = false;
            }
        };
    };
    isCurrentOrNextPredicate = (currentTimeInSeconds, startTimeInSeconds, durationInSeconds) => {
        const endTime = startTimeInSeconds + durationInSeconds;
        return currentTimeInSeconds < endTime;
    };
    getCurrentOrNextAdBreak = (avails, currentTime) => {
        return find(avails, (avail) => this.isCurrentOrNextPredicate(currentTime, avail.startTimeInSeconds, avail.durationInSeconds));
    };
    getCurrentOrNextAd = (adList, currentTime) => {
        let firstAdIndex = 0;
        const firstAd = find(adList, (ad, index) => {
            if (this.isCurrentOrNextPredicate(currentTime, ad.startTimeInSeconds, ad.durationInSeconds)) {
                firstAdIndex = index;
                return true;
            }
            return false;
        });
        return {
            ad: firstAd,
            index: firstAdIndex,
        };
    };
    eventTypeHandlerMap = {
        resume: (ad, event) => ({
            adId: ad.adId,
            name: 'resume',
            invokedByPosition: false,
            addListener: () => addStateChangeListener(State.playerStatus, this.getResumeHandler(event)),
            removeListener: () => removeStateChangeListener(State.playerStatus, this.getResumeHandler(event)),
        }),
        pause: (ad, event) => ({
            adId: ad.adId,
            name: 'pause',
            invokedByPosition: false,
            addListener: () => addStateChangeListener(State.playerStatus, this.getPauseHandler(event)),
            removeListener: () => removeStateChangeListener(State.playerStatus, this.getPauseHandler(event)),
        }),
        start: (ad, event) => ({
            adId: ad.adId,
            name: 'start',
            invokedByPosition: true,
            positionEventCallback: () => {
                this.invokeTracking(event);
            },
        }),
        complete: (ad, event) => ({
            adId: ad.adId,
            name: 'complete',
            invokedByPosition: true,
            positionEventCallback: () => {
                this.invokeTracking(event);
                this.invokeReporting({
                    ad,
                    eventStatus: Events.Status.ended,
                    eventId: event.eventId,
                });
            },
        }),
        creativeView: (ad, event) => ({
            adId: ad.adId,
            name: 'creativeView',
            invokedByPosition: false,
            addListener: () => this.invokeTracking(event),
        }),
        impression: (ad, event) => ({
            adId: ad.adId,
            name: 'impression',
            invokedByPosition: false,
            addListener: () => this.invokeTracking(event),
        }),
        firstQuartile: (ad, event) => ({
            adId: ad.adId,
            name: 'firstQuartile',
            invokedByPosition: true,
            positionEventCallback: () => {
                this.invokeTracking(event);
                this.invokeReporting({
                    eventStatus: Events.Status.firstQuartile,
                    eventId: event.eventId,
                });
            },
        }),
        midpoint: (ad, event) => ({
            adId: ad.adId,
            name: 'midpoint',
            invokedByPosition: true,
            positionEventCallback: () => {
                this.invokeTracking(event);
                this.invokeReporting({
                    eventStatus: Events.Status.midPoint,
                    eventId: event.eventId,
                });
            },
        }),
        thirdQuartile: (ad, event) => ({
            adId: ad.adId,
            name: 'thirdQuartile',
            invokedByPosition: true,
            positionEventCallback: () => {
                this.invokeTracking(event);
                this.invokeReporting({
                    eventStatus: Events.Status.thirdQuartile,
                    eventId: event.eventId,
                });
            },
        }),
    };
}
